import { TZDate } from '@date-fns/tz'
import { tz } from '@date-fns/tz/tz'
import clsx from 'clsx'
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  isAfter,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isToday,
  isValid,
  isWeekend,
  isWithinInterval,
  setDate,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns'
import type {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  Ref,
  RefObject,
} from 'react'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import useDateFormatters from '../hooks/useDateFormatters'
import useDateParsers from '../hooks/useDateParsers'
import IconButton from '../IconButton/IconButton'
import ChevronLeft from '../icons/ChevronLeft'
import ChevronRight from '../icons/ChevronRight'
import getCurrentLocale from '../utilities/getCurrentLocale'
import getStartOfWeek from '../utilities/getStartOfWeek'

interface CalendarizeProps {
  condensed: boolean
  date: Date
  intl: IntlShape
  timezone: string
}

export interface ChangeEvent {
  target: {
    value: Date | (Date | null)[] | null
  }
}

export interface DatePickerProps {
  /**
   * Reference to a button element that triggers clearing the selected date(s)
   */
  clearRef?: Ref<HTMLButtonElement>
  /**
   * When true, displays a condensed calendar view showing only one week instead of the full month
   * @default false
   */
  condensed?: boolean
  /**
   * The initially selected date(s).
   * Can be a single Date for day mode, a tuple [startDate, endDate] for range/week modes, or null for no initial selection
   * @default new Date()
   */
  initialDate?: [Date, Date] | Date | null
  /**
   * IANA timezone identifier (e.g., 'America/New_York').
   * Defaults to the user's local timezone if not provided or invalid
   */
  initialTimezone?: string
  /**
   * Only used internally inside the `DatePickerInput` component
   */
  inputRef?: RefObject<HTMLInputElement>
  /**
   * Only used internally inside the `DatePickerInput` component
   */
  inputValueRef?: RefObject<string>
  /**
   * Selection mode for the date picker: Day=0 (single date), Week=1 (full week), or Range=2 (custom date range)
   * @default Modes.Day
   */
  mode?: Modes
  /**
   * Callback function invoked when the selected date(s) change
   */
  onChange?: (event: ChangeEvent) => void
  /**
   * Whether the date picker is currently open/visible
   */
  open?: boolean
}

interface DateRange {
  end: Date | null
  start: Date | null
}

interface InRangeBoundaries {
  end: boolean
  inside: boolean
  start: boolean
}

export enum Modes {
  Day,
  Week,
  Range,
}

function calendarize({
  condensed,
  date,
  intl,
  timezone,
}: CalendarizeProps): Date[][] {
  const locale = getCurrentLocale(intl.locale)
  const weekStartsOn = getStartOfWeek(intl.locale)

  let i = 0
  let dates: Date[][] = []
  let current = condensed
    ? startOfWeek(date, { locale, weekStartsOn, in: tz(timezone) })
    : startOfWeek(startOfMonth(date), {
        locale,
        weekStartsOn,
        in: tz(timezone),
      })

  const final = condensed
    ? addDays(endOfWeek(date, { locale, weekStartsOn }), 1)
    : addDays(
        endOfWeek(endOfMonth(date), { locale, weekStartsOn, in: tz(timezone) }),
        1,
      )
  const difference =
    differenceInCalendarDays(final, current, { in: tz(timezone) }) / 7

  for (i; i < difference; i++) {
    let o = 0
    let week: Date[] = []

    for (o; o < 7; o++) {
      week = [...week, current]
      current = addDays(current, 1)
    }

    dates = [...dates, week]
  }

  return dates
}

function DatePicker({
  clearRef,
  condensed = false,
  initialDate = new Date(),
  initialTimezone,
  inputRef,
  inputValueRef,
  mode = Modes.Day,
  onChange,
  open,
}: DatePickerProps): JSX.Element {
  const intl = useIntl()
  const {
    formatDateLocal,
    formatMonthAndYearLong,
    formatISODate,
    formatWeekOfYear,
    formatDayOfMonth,
    formatYearAndMonth,
    formatDayOfWeek,
  } = useDateFormatters()
  const { parseDateLocal, validateTimezone } = useDateParsers()

  const timezone =
    (validateTimezone(initialTimezone) && initialTimezone) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone

  const initializeDate = (initial: Date | null) => {
    const date = initial || new Date()

    return new TZDate(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      timezone,
    )
  }

  const focusRef = useRef(true)
  const [currentDate, setCurrentDate] = useState(
    initializeDate(Array.isArray(initialDate) ? initialDate[0] : initialDate),
  )
  const [enteredDate, setEnteredDate] = useState<Date | null>(null)
  const [rangedDates, setRangedDates] = useState<DateRange>(() => ({
    end:
      mode === Modes.Range && Array.isArray(initialDate)
        ? initialDate[1]
        : null,
    start:
      mode === Modes.Range && Array.isArray(initialDate)
        ? initialDate[0]
        : null,
  }))
  const [selectedDate, setSelectedDate] = useState(
    initialDate
      ? initializeDate(
          Array.isArray(initialDate) ? initialDate[0] : initialDate,
        )
      : null,
  )

  const weeks = calendarize({
    condensed,
    date: currentDate,
    intl,
    timezone,
  })
  const [headers] = weeks
  const month = setDate(currentDate, 15, { in: tz(timezone) })
  const modes = {
    day: mode === Modes.Day,
    week: mode === Modes.Week,
    range: mode === Modes.Range,
    dayOrRange: mode === Modes.Day || mode === Modes.Range,
  }

  const parseValue = useCallback(
    (value: string): (TZDate | null)[] => {
      if (mode === Modes.Range || mode === Modes.Week) {
        if (!value) {
          return [null, null]
        }

        const dates = value.split(' — ')

        const locale = getCurrentLocale(intl.locale)
        const weekStartsOn = getStartOfWeek(intl.locale)

        const start =
          mode === Modes.Week
            ? startOfWeek(parseDateLocal(dates[0]), {
                locale,
                weekStartsOn,
              })
            : parseDateLocal(dates[0])

        const end =
          mode === Modes.Week ? endOfWeek(start) : parseDateLocal(dates[1])

        if (!isValid(start) || !isValid(end)) {
          return []
        }

        return [start as TZDate, end as TZDate]
      }

      const date = value ? parseDateLocal(value) : null

      if (date && !isValid(date)) {
        return []
      }

      return [date as TZDate]
    },
    [mode, parseDateLocal],
  )

  useEffect(() => {
    if (mode === Modes.Range) {
      const event: ChangeEvent = {
        target: {
          value: [rangedDates.start, rangedDates.end],
        },
      }

      onChange?.(event)
    } else {
      const event: ChangeEvent = {
        target: {
          value: selectedDate,
        },
      }

      onChange?.(event)
    }
  }, [mode, rangedDates.end, rangedDates.start, selectedDate, intl.locale])
  // TODO: Having the onChange function in the dependency array was causing an maximum update depth error. We've removed it temporarily as we do not want to force developers to have to remember to memoize this callback all the time. We will come back to this and come up with a more foolproof plan later on.

  useLayoutEffect(() => {
    // When DatePicker is used with input and the input value changes, adjust selected date(s)
    if (open && inputRef?.current) {
      if (inputValueRef?.current === inputRef.current.value) {
        return
      }

      let dates = parseValue(inputRef.current.value)

      if (dates.length) {
        if (mode === Modes.Range) {
          if (isAfter(dates[0] as Date, dates[1] as Date)) {
            dates = [dates[1], dates[0]]
          }

          setRangedDates({ start: dates[0], end: dates[1] })
        } else {
          setSelectedDate(dates[0])
        }

        // When the range provided didn't align to a week and needed to be adjusted,
        // updating the input value to the correct string avoids onChange handler being
        // triggered unnecessarily thus causing the picker to close right after being opened
        if (
          (mode === Modes.Week || mode === Modes.Range) &&
          dates[0] &&
          dates[1]
        ) {
          inputRef.current.value =
            formatDateLocal(dates[0]) + ' — ' + formatDateLocal(dates[1])
        }

        // Open the calendar on the selected date
        focusRef.current = true
        setCurrentDate(dates[0] || new TZDate(new Date(), timezone))
      }
    }
  }, [mode, open, intl.locale, inputRef, parseValue, inputValueRef])

  useLayoutEffect(() => {
    if (focusRef.current) {
      focusRef.current = false

      return
    }

    const selector = `time[datetime="${formatISODate(currentDate)}"]`
    const time = document.querySelector(selector) as HTMLTimeElement
    let selectable = time.parentElement as HTMLElement

    if (modes.week) {
      selectable = selectable.parentElement as HTMLElement
    }

    selectable.focus()
  }, [currentDate, formatISODate, modes.week])

  const next = (): void => {
    focusRef.current = true

    setCurrentDate((date) =>
      condensed ? addDays(date, 7) : addMonths(date, 1),
    )
  }

  const previous = (): void => {
    focusRef.current = true

    setCurrentDate((date) =>
      condensed ? subDays(date, 7) : subMonths(date, 1),
    )
  }

  const clearDate = () => {
    if (modes.range) {
      setRangedDates({ end: null, start: null })
    } else {
      setSelectedDate(null)
    }
  }

  const select = (
    e: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLButtonElement>,
  ): void => {
    if (['keypress', 'keydown', 'keyup'].includes(e.nativeEvent.type)) {
      const key = (e as KeyboardEvent<HTMLDivElement>).key

      if (key !== 'Enter' && key !== ' ') {
        setCurrentDate((date) => {
          switch (key) {
            case 'ArrowDown':
              return startOfWeek(addDays(date, 7))
            case 'ArrowUp':
              return startOfWeek(subDays(date, 7))
            default:
              return date
          }
        })

        return
      }
    }

    const time = e.currentTarget.querySelector('time') as HTMLTimeElement
    const dateTime = time.dataset?.datetime || formatYearAndMonth(new Date())
    const [year, month, day] = dateTime.split('-').map(Number)
    const date = new TZDate(year, month - 1, day, timezone)

    if (modes.range) {
      if (
        (rangedDates.end === null && rangedDates.start === null) ||
        (rangedDates.end !== null && rangedDates.start !== null)
      ) {
        setCurrentDate(date)
        setRangedDates({ start: date, end: null })
      }

      if (rangedDates.end === null && rangedDates.start !== null) {
        setRangedDates((dates) => {
          const after = isAfter(dates.start as Date, date)

          return {
            end: after ? dates.start : date,
            start: after ? date : dates.start,
          }
        })
      }
    } else {
      setSelectedDate(date)
      setCurrentDate(date)
    }
  }

  const keydown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const key = event.key

    setCurrentDate((date) => {
      switch (key) {
        case 'ArrowLeft':
          return subDays(date, 1)
        case 'ArrowRight':
          return addDays(date, 1)
        case 'ArrowDown':
          return addDays(date, 7)
        case 'ArrowUp':
          return subDays(date, 7)
        default:
          return date
      }
    })
  }

  const blur = (): void => {
    if (modes.range) {
      setEnteredDate(null)
    }
  }

  const enter = (
    e: FocusEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>,
  ): void => {
    const time = e.currentTarget.querySelector('time') as HTMLTimeElement
    const dateTime = time.dataset?.datetime || formatYearAndMonth(new Date())
    const [year, month, day] = dateTime.split('-').map(Number)

    setEnteredDate(new TZDate(year, month - 1, day, timezone))
  }

  const leave = (): void => {
    setEnteredDate(null)
  }

  const inRange = (day: Date): InRangeBoundaries => {
    const startDate = rangedDates.start
    const endDate = rangedDates.end || enteredDate

    if (startDate instanceof Date && endDate instanceof Date) {
      const after = isAfter(startDate, endDate)
      const interval = after
        ? { start: endDate, end: startDate }
        : { start: startDate, end: endDate }

      return {
        end: isSameDay(day, after ? startDate : endDate, { in: tz(timezone) }),
        inside:
          isWithinInterval(day, interval, { in: tz(timezone) }) ||
          isSameDay(day, startDate, { in: tz(timezone) }),
        start: isSameDay(day, after ? endDate : startDate, {
          in: tz(timezone),
        }),
      }
    }

    return { end: false, inside: false, start: false }
  }

  const isSameRangedDay = (day: Date): boolean =>
    (rangedDates.end instanceof Date &&
      isSameDay(day, rangedDates.end, { in: tz(timezone) })) ||
    (rangedDates.start instanceof Date &&
      isSameDay(day, rangedDates.start, { in: tz(timezone) }))

  return (
    <div
      className="max-w-xs p-6 font-sans rounded bg-white-100 shadow-1"
      onBlur={blur}
    >
      <div className="flex items-center justify-between">
        <time
          className="text-sm font-medium leading-4"
          data-datetime={formatYearAndMonth(month)}
          dateTime={formatYearAndMonth(month)}
        >
          {formatMonthAndYearLong(month)}
        </time>
        <div className="flex items-center justify-between w-16">
          {clearRef ? (
            <button className="hidden" onClick={clearDate} ref={clearRef} />
          ) : null}
          <IconButton
            aria-label={intl.formatMessage({ id: 'DatePicker.previous' })}
            onClick={previous}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            aria-label={intl.formatMessage({ id: 'DatePicker.next' })}
            onClick={next}
          >
            <ChevronRight />
          </IconButton>
        </div>
      </div>
      <div className="flex mt-4 text-center">
        {headers.map((header) => (
          <div
            className="text-xs leading-4 w-1/7 text-black-90"
            key={formatDayOfWeek(header)}
          >
            {formatDayOfWeek(header)}
          </div>
        ))}
      </div>
      {weeks.map((days) => {
        const currentWeek = isSameWeek(days[0], currentDate)
        const sameWeek = selectedDate
          ? isSameWeek(days[0], selectedDate)
          : false

        return (
          <div
            className={clsx(
              'flex mt-2 text-center',
              modes.week
                ? 'group rounded-full active:bg-primary-20 focus:bg-primary-10 focus:outline-none hover:bg-primary-10'
                : 'focus:outline-none',
              sameWeek && modes.week
                ? 'bg-primary-main text-white-100 active:text-black-90 hover:text-black-90 focus:text-black-90'
                : null,
            )}
            key={formatWeekOfYear(days[0])}
            onKeyDown={modes.week ? select : undefined}
            role="button"
            tabIndex={modes.dayOrRange ? -1 : currentWeek ? 0 : -1}
          >
            {days.map((day) => {
              const currentDay = isSameDay(day, currentDate, {
                in: tz(timezone),
              })
              const sameMonth = isSameMonth(day, month, { in: tz(timezone) })
              const sameDay = modes.day
                ? selectedDate
                  ? isSameDay(day, selectedDate, { in: tz(timezone) })
                  : false
                : isSameRangedDay(day)
              const today = isToday(day, { in: tz(timezone) })
              const weekend = isWeekend(day, { in: tz(timezone) })
              const { end, inside, start } = inRange(day)

              return (
                <button
                  className={clsx(
                    'flex justify-center w-1/7 focus:outline-none',
                    modes.dayOrRange ? 'group' : null,
                    sameDay && modes.dayOrRange
                      ? 'text-white-100 hover:text-black-90 focus:text-black-90'
                      : null,
                    sameMonth && !sameDay && !weekend && modes.dayOrRange
                      ? 'text-black-90'
                      : null,
                    !sameMonth && !sameDay && !condensed && modes.dayOrRange
                      ? 'text-black-45'
                      : null,
                    weekend && !sameDay && modes.dayOrRange
                      ? 'text-black-45'
                      : null,
                    weekend && !sameWeek && modes.week ? 'text-black-45' : null,
                    inside && modes.range
                      ? 'bg-primary-10 first:rounded-l-full last:rounded-r-full'
                      : null,
                    inside && end && modes.range ? 'rounded-r-full' : null,
                    inside && start && modes.range ? 'rounded-l-full' : null,
                  )}
                  key={formatISODate(day)}
                  onClick={select}
                  onFocus={modes.range ? enter : undefined}
                  onKeyDown={modes.dayOrRange ? keydown : undefined}
                  onMouseEnter={modes.range ? enter : undefined}
                  onMouseLeave={modes.range ? leave : undefined}
                  tabIndex={modes.week ? -1 : currentDay ? 0 : -1}
                  type="button"
                >
                  <time
                    className={clsx(
                      'flex items-center justify-center w-8 h-8 text-sm leading-4 rounded-full',
                      modes.dayOrRange
                        ? 'group-active:bg-primary-20 group-focus:bg-primary-10 group-hover:bg-primary-10'
                        : null,
                      sameDay && modes.dayOrRange ? 'bg-primary-main' : null,
                      today && modes.dayOrRange ? 'bg-black-5' : null,
                      today && !sameWeek && modes.week
                        ? 'bg-black-5 group-active:bg-primary-20'
                        : null,
                      today && sameWeek && modes.week
                        ? 'bg-primary-dark group-active:bg-primary-20 group-focus:bg-primary-15 group-hover:bg-primary-15'
                        : null,
                    )}
                    data-datetime={formatISODate(day)}
                    dateTime={formatISODate(day)}
                  >
                    {formatDayOfMonth(day)}
                  </time>
                </button>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default DatePicker
